import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * The shadcn convention: merge conditional classes and let a later utility win over an earlier
 * conflicting one. Without `twMerge`, `cn('p-2', 'p-4')` emits both and which applies depends on
 * stylesheet order rather than on the call.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Bytes as KB, for the size lint and the version list. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

/**
 * A date as "12 Aug 2026, 14:53".
 *
 * en-GB explicitly rather than the browser locale: this is an internal tool for one office, and a
 * developer on a machine set to en-US seeing month/day reversed is a real source of confusion when
 * reading a send schedule.
 */
export function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "3 minutes ago" for the autosave indicator, where absolute time is noise. */
export function formatRelative(iso: string | null): string {
  if (iso === null) return '—';

  const then = new Date(iso).getTime();

  if (Number.isNaN(then)) return '—';

  const seconds = Math.round((Date.now() - then) / 1000);

  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;

  return formatDateTime(iso);
}
